import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, ButtonGroup, Divider } from "@mui/material";
import { trimContentToMaxChars } from "./textareahelper";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  FormatQuote,
} from "@mui/icons-material";

export default function DetailsTextArea({
  editorRef,
  isDarkTheme,
  setDescription,
  populateData,
}) {
  const [localContent, setLocalContent] = useState("");
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const lastSelectionRef = useRef(null);

  const MAX_CHARS = 250;

  useEffect(() => {
    if (populateData && populateData?.description) {
      const content = populateData.description;
      setLocalContent(content);
      setCharCount(getTextContentLength(content));
      if (editorRef.current) {
        editorRef.current.innerHTML = content;
      }
    }
  }, [populateData, editorRef]);

  // Helper function to get text content length (without HTML tags)
  const getTextContentLength = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent?.length || 0;
  };

  const handleInput = (e) => {
    const content = e.target.innerHTML;
    const textLength = getTextContentLength(content);

    if (textLength > MAX_CHARS) {
      // Trim the content to max characters
      const trimmedContent = trimContentToMaxChars(content, MAX_CHARS);
      e.target.innerHTML = trimmedContent;
      setLocalContent(trimmedContent);
      setCharCount(MAX_CHARS);
      setDescription(trimmedContent);
    } else {
      setLocalContent(content);
      setCharCount(textLength);
      setDescription(content);
    }
  };

  // Function to trim HTML content while preserving structure

  const handleBlur = () => {
    setDescription(localContent);
    setIsEditorFocused(false);
  };

  const handleFocus = () => {
    setIsEditorFocused(true);
  };

  // Save selection when editor loses focus
  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      lastSelectionRef.current = selection.getRangeAt(0);
    }
  };

  // Restore selection when applying formatting
  const restoreSelection = () => {
    if (lastSelectionRef.current) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(lastSelectionRef.current);
    }
  };

  const formatText = (command, value = null) => {
    // Check character limit before formatting
    if (charCount >= MAX_CHARS && command !== "removeFormat") {
      return;
    }

    saveSelection();

    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.focus();
        restoreSelection();
        document.execCommand(command, false, value);
        updateContent();
        editorRef.current.focus();
      }
    }, 10);
  };

  const updateContent = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      const textLength = getTextContentLength(html);

      if (textLength > MAX_CHARS) {
        const trimmedContent = trimContentToMaxChars(html, MAX_CHARS);
        editorRef.current.innerHTML = trimmedContent;
        setLocalContent(trimmedContent);
        setCharCount(MAX_CHARS);
        setDescription(trimmedContent);
      } else {
        setLocalContent(html);
        setCharCount(textLength);
        setDescription(html);
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();

    if (charCount >= MAX_CHARS) return;

    const text = e.clipboardData.getData("text/plain");
    const availableChars = MAX_CHARS - charCount;

    if (availableChars > 0) {
      const textToInsert = text.substring(0, availableChars);
      document.execCommand("insertText", false, textToInsert);
      updateContent();
    }
  };

  // Function to handle list formatting properly
  const handleListFormat = (command) => {
    if (charCount >= MAX_CHARS) return;

    saveSelection();

    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.focus();
        restoreSelection();

        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const parentElement = range?.commonAncestorContainer?.parentElement;

          if (
            (command === "insertUnorderedList" &&
              parentElement?.tagName === "UL") ||
            (command === "insertOrderedList" && parentElement?.tagName === "OL")
          ) {
            document.execCommand("outdent", false, null);
          } else {
            document.execCommand(command, false, null);
          }
        } else {
          document.execCommand(command, false, null);
        }

        updateContent();
        editorRef.current.focus();
      }
    }, 10);
  };

  // Handle key down to prevent typing when at limit
  const handleKeyDown = (e) => {
    const currentText = editorRef.current?.textContent || "";

    // Prevent typing if at max characters (except for control keys)
    if (
      charCount >= MAX_CHARS &&
      e.key.length === 1 && // Single character keys
      !e.ctrlKey &&
      !e.metaKey &&
      !e.altKey &&
      e.key !== "Backspace" &&
      e.key !== "Delete" &&
      e.key !== "ArrowLeft" &&
      e.key !== "ArrowRight" &&
      e.key !== "ArrowUp" &&
      e.key !== "ArrowDown"
    ) {
      e.preventDefault();
    }
  };

  // Handle click on toolbar - don't blur the editor
  const handleToolbarClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Box
      sx={{
        border: isDarkTheme ? "1px solid #4E4E4E" : "1px solid lightgray",
        borderRadius: 2,
        backgroundColor: isDarkTheme ? "#333" : "#fff",
        overflow: "hidden",
      }}
      data-testid="text-box"
    >
      {/* EDITOR AREA - AT THE TOP */}
      <Box
        sx={{
          p: 2,
          minHeight: 200,
          maxHeight: 400,
          overflowY: "auto",
          borderBottom: isDarkTheme
            ? "1px solid #4E4E4E"
            : "1px solid lightgray",
        }}
      >
        <Box
          ref={editorRef}
          contentEditable
          data-testid="editable"
          onInput={handleInput}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          sx={{
            minHeight: 150,
            color: isDarkTheme ? "white" : "black",
            outline: "none",
            "& h1, & h2, & h3, & h4, & h5, & h6": {
              margin: "12px 0 8px 0",
              color: isDarkTheme ? "white" : "black",
            },
            "& p": {
              margin: "8px 0",
            },
            "& ul, & ol": {
              margin: "8px 0",
              paddingLeft: "24px",
            },
            "& li": {
              margin: "4px 0",
              display: "list-item",
            },
            "& ul": {
              listStyleType: "disc",
            },
            "& ol": {
              listStyleType: "decimal",
            },
            "& blockquote": {
              borderLeft: "3px solid #ccc",
              margin: "16px 0",
              paddingLeft: "16px",
              fontStyle: "italic",
              color: isDarkTheme ? "#ccc" : "#666",
            },
            "& a": {
              color: isDarkTheme ? "#90caf9" : "#1976d2",
              textDecoration: "underline",
            },
            "& img": {
              maxWidth: "100%",
              height: "auto",
              margin: "8px 0",
            },
          }}
        />
      </Box>

      {/* TOOLBAR - AT THE BOTTOM */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          p: 1,
        }}
        onClick={handleToolbarClick}
      >
        {/* Basic Formatting */}
        <ButtonGroup variant="outlined" size="small">
          <IconButton
            size="small"
            onClick={() => formatText("underline")}
            title="Underline"
            sx={{
              color: isDarkTheme ? "white" : "black",
              opacity: charCount >= MAX_CHARS ? 0.5 : 1,
            }}
            disabled={charCount >= MAX_CHARS}
          >
            <FormatUnderlined fontSize="small" />
          </IconButton>
        </ButtonGroup>

        <Divider orientation="vertical" flexItem />

        {/* Lists */}
        <ButtonGroup variant="outlined" size="small">
          <IconButton
            size="small"
            onClick={() => handleListFormat("insertUnorderedList")}
            title="Bullet List"
            sx={{
              color: isDarkTheme ? "white" : "black",
              opacity: charCount >= MAX_CHARS ? 0.5 : 1,
            }}
            disabled={charCount >= MAX_CHARS}
          >
            <FormatListBulleted fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleListFormat("insertOrderedList")}
            title="Numbered List"
            sx={{
              color: isDarkTheme ? "white" : "black",
              opacity: charCount >= MAX_CHARS ? 0.5 : 1,
            }}
            disabled={charCount >= MAX_CHARS}
          >
            <FormatListNumbered fontSize="small" />
          </IconButton>
        </ButtonGroup>

        <Divider orientation="vertical" flexItem />

        {/* Alignment */}
        <ButtonGroup variant="outlined" size="small">
          <IconButton
            size="small"
            onClick={() => formatText("justifyLeft")}
            title="Align Left"
            sx={{
              color: isDarkTheme ? "white" : "black",
              opacity: charCount >= MAX_CHARS ? 0.5 : 1,
            }}
            disabled={charCount >= MAX_CHARS}
          >
            <FormatAlignLeft fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => formatText("justifyCenter")}
            title="Align Center"
            sx={{
              color: isDarkTheme ? "white" : "black",
              opacity: charCount >= MAX_CHARS ? 0.5 : 1,
            }}
            disabled={charCount >= MAX_CHARS}
          >
            <FormatAlignCenter fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => formatText("justifyRight")}
            title="Align Right"
            sx={{
              color: isDarkTheme ? "white" : "black",
              opacity: charCount >= MAX_CHARS ? 0.5 : 1,
            }}
            disabled={charCount >= MAX_CHARS}
          >
            <FormatAlignRight fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => formatText("justifyFull")}
            title="Justify"
            sx={{
              color: isDarkTheme ? "white" : "black",
              opacity: charCount >= MAX_CHARS ? 0.5 : 1,
            }}
            disabled={charCount >= MAX_CHARS}
          >
            <FormatAlignJustify fontSize="small" />
          </IconButton>
        </ButtonGroup>
      </Box>

      {/* CHARACTER COUNTER */}
      {/* <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          px: 2,
          py: 1,
          borderTop: isDarkTheme ? "1px solid #4E4E4E" : "1px solid lightgray",
          backgroundColor: isDarkTheme ? "#444" : "#f5f5f5",
        }}
      >
        <Box
          sx={{
            fontSize: "0.75rem",
            color:
              charCount >= MAX_CHARS
                ? "error.main"
                : isDarkTheme
                ? "#ccc"
                : "#666",
            fontWeight: charCount >= MAX_CHARS ? "bold" : "normal",
          }}
        >
          {charCount}/{MAX_CHARS}
        </Box>
      </Box> */}
    </Box>
  );
}

DetailsTextArea.propTypes = {
  editorRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }).isRequired,
  isDarkTheme: PropTypes.bool.isRequired,
  setDescription: PropTypes.func.isRequired,
  populateData: PropTypes.shape({
    description: PropTypes.string,
  }),
};
